import type { Modification } from '@carboncopyco/types';
import { Badge } from '@carboncopyco/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import {
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RuleCardLockButton,
  RuleCardToggle,
  RulesContent,
} from '../RulesManager';
import { TransactionRulePreview } from '../TransactionRulePreview';

export interface TransactionListItemProps {
  transactionModification: Modification<'transaction_rule'>;
  onEdit: () => void;
  onToggleActivate: () => void;
  onToggleLock: () => void;
  onDelete: () => void;
  onDuplicate: (
    transactionModification: Modification<'transaction_rule'> & {
      isActive?: boolean;
      isLocked?: boolean;
    }
  ) => void;
}

export const TransactionListItem = ({
  transactionModification,
  onEdit,
  onToggleActivate,
  onToggleLock,
  onDelete,
  onDuplicate,
}: TransactionListItemProps) => {
  const { editorDataRulesAdmin } = useFlags();

  const dropdownItems = useMemo(
    () =>
      [
        {
          type: 'edit',
          onClick: onEdit,
        },
        editorDataRulesAdmin && {
          type: 'duplicate',
          onClick: () => onDuplicate(transactionModification),
        },
        editorDataRulesAdmin && {
          type: transactionModification.isLocked ? 'unlock' : 'lock',
          onClick: onToggleLock,
        },
        editorDataRulesAdmin && {
          type: 'delete',
          onClick: onDelete,
        },
      ].filter(Boolean) as RuleCardDropdownProps['items'],
    [
      onEdit,
      transactionModification,
      editorDataRulesAdmin,
      onToggleLock,
      onDelete,
      onDuplicate,
    ]
  );

  return (
    <RulesContent.List.Item
      id={transactionModification.id}
      header={
        <TransactionRulePreview
          transactionRule={transactionModification.content}
          disabled={!transactionModification.isActive}
        />
      }
      iconRight={
        !transactionModification.isActive && (
          <div className="group-hover:hidden block">
            <Badge
              label="off"
              variant="disabled"
            />
          </div>
        )
      }
      expandable={false}
      action={
        transactionModification.isLocked ? (
          <RuleCardLockButton onClick={onToggleLock} />
        ) : (
          <>
            <RuleCardToggle
              checked={transactionModification.isActive}
              onChange={onToggleActivate}
            />
            <RuleCardDropdown items={dropdownItems} />
          </>
        )
      }
    />
  );
};
